<script>
  import { link, location } from 'svelte-spa-router';
  import { tick } from 'svelte';
  import {
    Home, FileText, FilePlus, Files, Activity, ChevronRight, ChevronDown, Sun, Moon, LogOut, KeyRound,
    Building2, Users, Briefcase, CalendarClock, Landmark, Settings as SettingsIcon, Eye, Menu, X,
  } from '../lib/icons/index.js';
  import Brand from './Brand.svelte';
  import { colorMode, initAppearance } from '../stores/theme.js';
  import { logout, authStore } from '../stores/auth.js';
  import { isAdmin, canViewDiagnostics, isCommercialista } from '../stores/roles.js';
  import { capabilities, canWriteInCompany } from '../stores/capabilities.js';
  import { bilancioPermissions } from '../lib/bilancio.js';
  import { hasFeature } from '../lib/features.js';
  import { companies, activeCompany, activeCompanyId, setActiveCompany } from '../stores/company.js';
  import { unreadInbound, refreshUnread } from '../stores/unread.js';
  import { badgeText } from '../lib/read-state.js';
  import { inertOutside, returnFocus, trapTab } from '../lib/modal.js';
  import { focusMainNow, focusMainOnNextPage, takePendingMainFocus } from '../lib/main-focus.js';
  import { t } from '../i18n/index.js';
  import { onMount } from 'svelte';

  let showCompanySwitcher = false;
  let navOpen = false;
  let menuButton;
  let sidebar;
  let scrim;

  // Below 900px the rail is a drawer over the page. While it is open it is
  // modal: the page behind is inert, Tab stays inside it, and widening the
  // window past the breakpoint closes it rather than leaving the page inert
  // behind a rail that is no longer a drawer.
  const DRAWER_QUERY = '(width < 56.25rem)';
  let restoreInert = null;

  onMount(() => {
    initAppearance();
    takePendingMainFocus(document.getElementById('main-content'));
    const narrow = window.matchMedia(DRAWER_QUERY);
    const onChange = () => { if (!narrow.matches) closeNav(); };
    narrow.addEventListener('change', onChange);
    return () => {
      narrow.removeEventListener('change', onChange);
      restoreInert?.();
    };
  });

  $: if (navOpen && sidebar && !restoreInert) {
    restoreInert = inertOutside(sidebar, { except: [scrim] });
  } else if (!navOpen && restoreInert) {
    restoreInert();
    restoreInert = null;
  }

  $: currentPath = $location;
  // The unread badge follows the active company and is re-read on every
  // navigation: opening a received invoice anywhere marks it read.
  $: currentPath, refreshUnread($activeCompanyId);

  /**
   * Breadcrumb: the active company, then the crumbs of the first matching
   * pattern. Order matters — the more specific pattern comes first.
   */
  const CRUMBS = [
    [/^\/$/, [['nav.dashboard']]],
    [/^\/invoices\/new/, [['nav.invoices', '/invoices'], ['nav.newInvoice']]],
    [/^\/invoices\/[^/]+/, [['nav.invoices', '/invoices'], ['nav.invoiceDetail']]],
    [/^\/invoices/, [['nav.invoices']]],
    [/^\/autofatture\/new/, [['nav.invoices', '/invoices'], ['nav.newAutofattura']]],
    [/^\/drafts\/[^/]+/, [['nav.invoices', '/invoices'], ['drafts.title', '/drafts'], ['nav.draftEdit']]],
    [/^\/drafts/, [['nav.invoices', '/invoices'], ['drafts.title']]],
    [/^\/scadenzario/, [['nav.scadenzario']]],
    [/^\/bilancio\/[^/]+/, [['nav.bilancio', '/bilancio'], ['bilancio.detail.breadcrumb']]],
    [/^\/bilancio/, [['nav.bilancio']]],
    [/^\/customers/, [['nav.customers']]],
    [/^\/settings\/appearance/, [['nav.settings', '/settings'], ['settings.tabs.appearance']]],
    [/^\/settings\/payment-methods/, [['nav.settings', '/settings'], ['settings.tabs.paymentMethods']]],
    [/^\/settings\/email/, [['nav.settings', '/settings'], ['settings.tabs.email']]],
    [/^\/settings\/import-invoices/, [['nav.settings', '/settings'], ['settings.tabs.importInvoices']]],
    [/^\/settings\/import-anagrafica/, [['nav.settings', '/settings'], ['settings.tabs.importAnagrafica']]],
    [/^\/settings\/users/, [['nav.settings', '/settings'], ['settings.tabs.users']]],
    [/^\/settings/, [['nav.settings']]],
    [/^\/companies\/[^/]+\/settings/, [['nav.companies', '/companies'], ['companySettings.breadcrumb']]],
    [/^\/companies/, [['nav.companies']]],
    [/^\/commercialista/, [['nav.commercialista']]],
    [/^\/diagnostics/, [['nav.diagnostics']]],
    [/^\/change-password/, [['nav.changePassword']]],
  ];

  $: crumbs = (() => {
    const match = CRUMBS.find(([pattern]) => pattern.test(currentPath));
    const list = (match ? match[1] : [['app.notFoundTitle']]).map(([key, href]) => ({ label: $t(key), href }));
    // On a company's settings page the last crumb names the company being
    // edited, which is not necessarily the active one. Admins hold the whole
    // list; until it loads, the generic label stands in.
    const target = /^\/companies\/([^/]+)\/settings/.exec(currentPath);
    if (target) {
      const company = $companies.find((c) => c.id === target[1]);
      if (company) list[list.length - 1].label = company.ragioneSociale;
    }
    return list;
  })();

  function isActive(path) {
    if (path === '/') return currentPath === '/';
    return currentPath === path || currentPath.startsWith(`${path}/`);
  }

  async function openNav() {
    navOpen = true;
    await tick();
    sidebar?.querySelector('a, button')?.focus();
  }

  function closeNav({ restoreFocus = false } = {}) {
    if (!navOpen) return;
    navOpen = false;
    restoreInert?.();
    restoreInert = null;
    if (restoreFocus) returnFocus(menuButton);
  }

  /**
   * A navigation link in the rail or the drawer: close the drawer, then put
   * focus on the page the link leads to (lib/main-focus.js) instead of
   * leaving it on <body> when this Layout and its link are replaced.
   */
  async function followLink(event) {
    // A modified click opens a new tab and leaves this page where it is.
    if (event?.ctrlKey || event?.metaKey || event?.shiftKey || event?.button > 0) return;
    closeNav();
    const href = event?.currentTarget?.getAttribute?.('href') ?? '';
    const target = href.replace(/^#/, '') || '/';
    if (target === currentPath) {
      await tick();
      focusMainNow(document.getElementById('main-content'));
    } else {
      focusMainOnNextPage();
    }
  }

  function handleKeydown(event) {
    if (!navOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeNav({ restoreFocus: true });
    } else if (event.key === 'Tab') {
      trapTab(event, sidebar);
    }
  }

  async function handleLogout() {
    await logout();
  }

  const year = new Date().getFullYear();
</script>

<svelte:window on:keydown={handleKeydown} />

<a class="skip-link" href="#main-content" on:click|preventDefault={() => document.getElementById('main-content')?.focus()}>{$t('nav.skipToContent')}</a>

<div class="app-shell" class:nav-open={navOpen}>
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="shell-scrim" bind:this={scrim} on:click={() => closeNav({ restoreFocus: true })}></div>

  <aside id="app-sidebar" class="sidebar" bind:this={sidebar} aria-label={$t('nav.sidebarLabel')}>
    <button type="button" class="icon-button drawer-close" aria-label={$t('nav.closeMenu')}
      title={$t('nav.closeMenu')} on:click={() => closeNav({ restoreFocus: true })}>
      <X size={20} aria-hidden="true" />
    </button>
    <Brand href="/" onNavigate={followLink} />

    {#if $companies.length > 0}
      <div class="workspace-card">
        <button type="button" class="workspace-toggle" aria-expanded={showCompanySwitcher}
          aria-controls="workspace-list" on:click={() => showCompanySwitcher = !showCompanySwitcher}>
          <span class="min-w-0">
            <span class="workspace-label">{$t('nav.workspaceLabel')}</span>
            <span class="workspace-name">{$activeCompany?.ragioneSociale || $t('nav.selectCompany')}</span>
          </span>
          <ChevronDown size={16} class="shrink-0 transition-transform {showCompanySwitcher ? 'rotate-180' : ''}" aria-hidden="true" />
        </button>
        {#if showCompanySwitcher}
          <div id="workspace-list" class="workspace-list">
            {#each $companies as company}
              <button type="button" class="workspace-option" aria-current={company.id === $activeCompanyId ? 'true' : undefined}
                on:click={() => { setActiveCompany(company.id); showCompanySwitcher = false; }}>
                {company.ragioneSociale}
              </button>
            {/each}
          </div>
        {/if}
      </div>
    {:else if $isAdmin}
      <!-- No company yet: teach the prerequisite up front instead of letting
           downstream screens block silently. -->
      <div class="workspace-card">
        <a href="/companies" use:link class="workspace-toggle" on:click={followLink}>
          <span class="min-w-0">
            <span class="workspace-label">{$t('nav.workspaceLabel')}</span>
            <span class="workspace-name">{$t('companies.emptyHint')}</span>
          </span>
          <Building2 size={16} class="shrink-0" aria-hidden="true" />
        </a>
      </div>
    {/if}

    <nav class="nav-group" aria-labelledby="nav-group-admin">
      <span id="nav-group-admin" class="nav-heading">{$t('nav.groupAdmin')}</span>

      <!-- Says the mode out loud. A read-only account otherwise discovers it
           by losing a form, and every hidden button looks like a bug. -->
      {#if $capabilities && !$canWriteInCompany}
        <p class="nav-note">
          <Eye size={14} class="shrink-0" aria-hidden="true" />
          <span>{$t('nav.readOnlyBadge')}</span>
        </p>
      {/if}

      <a href="/" use:link class="nav-item" class:nav-item-active={isActive('/')}
        aria-current={isActive('/') ? 'page' : undefined} on:click={followLink}>
        <Home size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.dashboard')}</span>
      </a>

      <a href="/invoices" use:link class="nav-item"
        class:nav-item-active={isActive('/invoices') && !isActive('/invoices/new')}
        aria-current={isActive('/invoices') && !isActive('/invoices/new') ? 'page' : undefined}
        on:click={followLink}>
        <FileText size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.invoices')}</span>
        {#if $unreadInbound > 0}
          <span class="nav-count" title={$t('nav.unreadInvoices', { n: $unreadInbound })}
            aria-label={$t('nav.unreadInvoices', { n: $unreadInbound })}>{badgeText($unreadInbound)}</span>
        {/if}
      </a>

      <!-- Persistent entry points for writing, hidden from a role that cannot
           write: the form used to open for a commercialista and refuse only on
           save, after the whole document had been typed. -->
      {#if $canWriteInCompany}
        <a href="/invoices/new" use:link class="nav-item nav-item-sub" class:nav-item-active={isActive('/invoices/new')}
          aria-current={isActive('/invoices/new') ? 'page' : undefined} on:click={followLink}>
          <FilePlus size={16} class="nav-icon" aria-hidden="true" />
          <span>{$t('nav.newInvoice')}</span>
        </a>
        <a href="/drafts" use:link class="nav-item nav-item-sub" class:nav-item-active={isActive('/drafts')}
          aria-current={isActive('/drafts') ? 'page' : undefined} on:click={followLink}>
          <Files size={16} class="nav-icon" aria-hidden="true" />
          <span>{$t('drafts.title')}</span>
        </a>
      {/if}

      <!-- Scadenzario reads the same invoices from the other end — by payment
           date rather than by document — so it sits next to Fatture. -->
      <a href="/scadenzario" use:link class="nav-item" class:nav-item-active={isActive('/scadenzario')}
        aria-current={isActive('/scadenzario') ? 'page' : undefined} on:click={followLink}>
        <CalendarClock size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.scadenzario')}</span>
      </a>

      <!-- The filed balance sheet: an experimental feature, so the entry exists
           only for an account that holds it (hasFeature) — and then, because
           every company role reads it, it follows the role in the active
           company, not the global one. -->
      {#if hasFeature($capabilities, 'bilancio') && bilancioPermissions($capabilities?.role).read}
        <a href="/bilancio" use:link class="nav-item" class:nav-item-active={isActive('/bilancio')}
          aria-current={isActive('/bilancio') ? 'page' : undefined} on:click={followLink}
          title={$t('features.bilancio.description')}>
          <Landmark size={18} class="nav-icon" aria-hidden="true" />
          <span>{$t('nav.bilancio')}<span class="sr-only"> ({$t('features.badge.experimental')})</span></span>
        </a>
      {/if}

      <a href="/customers" use:link class="nav-item" class:nav-item-active={isActive('/customers')}
        aria-current={isActive('/customers') ? 'page' : undefined} on:click={followLink}>
        <Users size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.customers')}</span>
      </a>

      {#if $isCommercialista || $isAdmin}
        <!-- "Commercialista" on its own says nothing about what the entry
             does; the glossary entry on the delega becomes the tooltip. -->
        <a href="/commercialista" use:link class="nav-item" class:nav-item-active={isActive('/commercialista')}
          aria-current={isActive('/commercialista') ? 'page' : undefined}
          title={$t('glossary.delega')} on:click={followLink}>
          <Briefcase size={18} class="nav-icon" aria-hidden="true" />
          <span>{$t('nav.commercialista')}</span>
        </a>
      {/if}
    </nav>

    {#if $isAdmin || $canViewDiagnostics}
      <nav class="nav-group" aria-labelledby="nav-group-system">
        <span id="nav-group-system" class="nav-heading">{$t('nav.groupSystem')}</span>
        {#if $isAdmin}
          <a href="/companies" use:link class="nav-item" class:nav-item-active={isActive('/companies')}
            aria-current={isActive('/companies') ? 'page' : undefined} on:click={followLink}>
            <Building2 size={18} class="nav-icon" aria-hidden="true" />
            <span>{$t('nav.companies')}</span>
          </a>
        {/if}
        {#if $canViewDiagnostics}
          <a href="/diagnostics" use:link class="nav-item" class:nav-item-active={isActive('/diagnostics')}
            aria-current={isActive('/diagnostics') ? 'page' : undefined}
            title={$t('glossary.diagnostica')} on:click={followLink}>
            <Activity size={18} class="nav-icon" aria-hidden="true" />
            <span>{$t('nav.diagnostics')}</span>
          </a>
        {/if}
      </nav>
    {/if}

    <div class="sidebar-bottom">
      <!-- Payment methods, imports, appearance and users live here as tabs; the
           page hides the tabs a role cannot use. -->
      <a href="/settings" use:link class="nav-item" class:nav-item-active={isActive('/settings')}
        aria-current={isActive('/settings') ? 'page' : undefined} on:click={followLink}>
        <SettingsIcon size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.settings')}</span>
      </a>

      {#if $authStore.user}
        <div class="sidebar-account">
          <span class="sidebar-account-email">{$authStore.user.email}</span>
          <!-- Server and interface are released separately: showing both
               versions saves wondering which one is stale. -->
          <span class="sidebar-account-versions" title={$t('nav.versionsHint')}>
            {$t('nav.versions', { server: $authStore.user.serverVersion || '—', ui: __APP_VERSION__ })}
          </span>
        </div>
      {/if}

      <a href="/change-password" use:link class="nav-item" class:nav-item-active={isActive('/change-password')}
        aria-current={isActive('/change-password') ? 'page' : undefined} on:click={followLink}>
        <KeyRound size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.changePassword')}</span>
      </a>

      <button type="button" class="nav-item" on:click={handleLogout}>
        <LogOut size={18} class="nav-icon" aria-hidden="true" />
        <span>{$t('nav.logout')}</span>
      </button>

      <p class="sidebar-footnote">
        <span>{$t('nav.copyright', { year })}</span>
        <a href="https://webdeploy.it" target="_blank" rel="noopener noreferrer"
          aria-label={$t('nav.copyrightLinkLabel')}>webdeploy</a>
      </p>
    </div>
  </aside>

  <div class="main-column">
    <header class="topbar">
      <button type="button" class="icon-button menu-button" bind:this={menuButton}
        aria-label={navOpen ? $t('nav.closeMenu') : $t('nav.openMenu')}
        aria-expanded={navOpen} aria-controls="app-sidebar"
        on:click={() => (navOpen ? closeNav() : openNav())}>
        <Menu size={20} aria-hidden="true" />
      </button>

      <nav class="breadcrumb" aria-label={$t('nav.breadcrumbLabel')}>
        <ol>
          {#if $activeCompany}
            <li><span>{$activeCompany.ragioneSociale}</span></li>
          {/if}
          {#each crumbs as crumb, index}
            <li>
              {#if $activeCompany || index > 0}
                <ChevronRight size={13} class="breadcrumb-separator" aria-hidden="true" />
              {/if}
              {#if index === crumbs.length - 1}
                <span aria-current="page">{crumb.label}</span>
              {:else if crumb.href}
                <a href={crumb.href} use:link>{crumb.label}</a>
              {:else}
                <span>{crumb.label}</span>
              {/if}
            </li>
          {/each}
        </ol>
      </nav>

      <div class="topbar-actions">
        <button type="button" class="icon-button" on:click={() => colorMode.toggle()}
          aria-label={$colorMode === 'dark' ? $t('nav.lightMode') : $t('nav.darkMode')}
          title={$colorMode === 'dark' ? $t('nav.lightMode') : $t('nav.darkMode')}>
          {#if $colorMode === 'dark'}
            <Sun size={18} aria-hidden="true" />
          {:else}
            <Moon size={18} aria-hidden="true" />
          {/if}
        </button>
      </div>
    </header>

    <main id="main-content" class="main-content" tabindex="-1">
      <div class="fade-in">
        <slot />
      </div>
    </main>
  </div>
</div>
